import { routeNames, isAdminView, findRoutePath } from "@/router";
import { APIService } from "@/services/APIService.inject";
import { consts, IUser, Roles } from "@/services/Models";
import { AuthStore } from "@/stores";
import { AxiosResponse } from "axios";
import { inject, InjectionKey, ref } from "vue";
import { Router } from "vue-router";

const login = (api: APIService, auth: AuthStore, router: Router) => {
  // ログイン処理
  const signin = async (
    userID: string,
    password: string,
    mustBeAdmin = false
  ) => {
    const ret = await api
      .login(userID, password)
      .then((user) => {
        // ログイン先が違ったらログインさせない
        if (user !== undefined && isWrongRole(mustBeAdmin, user)) {
          return false;
        }
        auth.setUser(user);
        if (user === undefined) {
          return false;
        }
        return true;
      })
      .catch((_: AxiosResponse) => false);
    // ログインできたら監視開始
    if (ret) {
      tryStartMonitor();
    }
    return ret;
  };

  // ログアウト処理
  const beforeSignout = ref<() => boolean>();
  const signout = async () => {
    // ログアウト前に画面遷移確認
    const isGuard = beforeSignout.value?.() ?? true;
    if (!isGuard) {
      return false;
    }
    await api.logout().catch((_: AxiosResponse) => false);
    auth.logout();
    // 監視停止
    stopMonitor();
    return true;
  };

  // 認証情報を再取得
  const loadAuth = async () => {
    await api
      .iam()
      .then((user) => {
        auth.setUser(user);
      })
      .catch((ex: AxiosResponse) => console.log(ex));
    if (auth.state.isLoggedin) {
      tryStartMonitor();
    }
  };
  // パスワードリセット
  const reset = async (userID: string) => {
    await api.reset(userID);
  };
  // パスワード設定
  const setPassword = async (
    oldPassword: string,
    newPassword: string,
    checkPassword: string
  ) => {
    return await api.setPassword(oldPassword, newPassword, checkPassword);
  };

  // 認証状態を判定して正しくなければログイン画面のRouteNameを返す
  const authGuard = async (routeName: string) => {
    // 今の認証ユーザを取得
    await loadAuth();

    // 判定対象が管理者画面かユーザ画面かを取得
    const isInAdminView = isAdminView(routeName);

    // ログイン済みかどうか
    if (auth.state.isLoggedin && auth.state.iam) {
      // ログイン済みでもログイン先と権限が一致していなかったらログアウトさせる
      const isWrong = isWrongRole(isInAdminView, auth.state.iam);
      // 一致してたらOK
      if (!isWrong) {
        return undefined;
      }
    }
    // サーバに対してログアウトはしない
    // なぜなら同じブラウザの別タブで別ユーザでログインした場合そっちもログアウトしてしまうから
    auth.logout();

    // それぞれのログイン画面に飛ばす
    if (isInAdminView) {
      return routeNames.AdminLogin;
    } else {
      return routeNames.Login;
    }
  };

  // 画面遷移時にログイン状態を判定して正しくなければLogin画面に戻す
  router.beforeResolve(async (to, from, next) => {
    const loginRoute = await authGuard(to.name?.toString() ?? "");
    const isAuthed = loginRoute === undefined;
    const isGuard = !isAuthed && to.name != loginRoute;
    // ログイン画面へ飛ばす
    if (isGuard) {
      const route = findRoutePath(loginRoute ?? ""); // isGuardの条件で`loginRoute`がundefinedでない事は保証されてるけどlintエラーになるので??で回避
      next(route);
    }

    // 問題なければ画面遷移
    // ただし認証が正しくてログイン画面に遷移しようとしている場合はTop画面へ遷移
    if (isAuthed && to.name == routeNames.Login) {
      const topPath = findRoutePath(routeNames.Top);
      next(topPath);
      return;
    } else if (isAuthed && to.name == routeNames.AdminLogin) {
      const topPath = findRoutePath(routeNames.AdminContract);
      next(topPath);
      return;
    }
    next();
  });

  // 認証セッションを監視して認証状態が正しくなければLogin画面に戻する
  const monitoringTask = ref(-1);
  const monitorSession = async () => {
    const loginRoute = await authGuard(
      router.currentRoute?.value?.name?.toString() ?? ""
    );
    if (loginRoute !== undefined) {
      router.push({ name: loginRoute });
    }
  };
  // 認証セッションのモニターを開始する(ログインから)
  const tryStartMonitor = () => {
    if (0 <= monitoringTask.value) return;
    if (!auth.state.isLoggedin) {
      return;
    }
    stopMonitor();
    monitoringTask.value = setInterval(
      monitorSession,
      consts.sessionMonitorIntervalMilliseconds
    );
    console.log("start monitor");
  };
  // 認証セッションのモニターを停止する(ログアウトまで)
  const stopMonitor = () => {
    clearInterval(monitoringTask.value);
    monitoringTask.value = -1;
    console.log("stop monitor");
  };

  // ログイン先とユーザロールの一致確認
  const isWrongRole = (mustBeAdmin: boolean, user: IUser) => {
    if (mustBeAdmin && user.role != Roles.Admin) {
      return true;
    } else if (!mustBeAdmin && user.role != Roles.None) {
      return true;
    }
    return false;
  };

  return {
    beforeSignout,
    signin,
    signout,
    loadAuth,
    reset,
    setPassword,
  };
};

export default login;

export type LoginStore = ReturnType<typeof login>;

export const loginStoreKey: InjectionKey<LoginStore> = Symbol("LoginStore");

export const useLoginStore = () => {
  const store = inject(loginStoreKey);
  if (!store) {
    throw new Error(`${loginStoreKey} is not provided`);
  }
  return store;
};
